import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ChatHeader } from '../ui/ChatHeader';
import { ChatMessage } from '../ui/ChatMessage';
import { ChatInput } from '../ui/ChatInput';
import { TokenLimitModal } from '../ui/TokenLimitModal';
import { ArtifactPanel } from '../../artifacts';
import { useChatMessages } from '../hooks/useChatMessages';
import { getSession } from '../../../services/chatService';
import { useAuth } from '../../../contexts/AuthContext';
import { CHAT_CONFIG, getInitials } from '../constants';

/**
 * ChatSessionPage - Página principal de sessão de chat
 */
export const ChatSessionPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const [sessionTitle, setSessionTitle] = useState('Chat');
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // User data from AuthContext
  const userName = user?.name || '';
  const userInitials = userName ? getInitials(userName) : 'U';

  const {
    messages,
    isLoading,
    isInitializing,
    error,
    userScrolled,
    activeArtifact,
    isArtifactLoading,
    isUpdating,
    tokenLimitError,
    messagesEndRef,
    setUserScrolled,
    setActiveArtifact,
    sendMessage,
    retryMessage,
    closeTokenLimitModal,
  } = useChatMessages(sessionId);

  // Load session title
  useEffect(() => {
    const loadSessionTitle = async () => {
      if (!sessionId) return;
      try {
        const response = await getSession(Number(sessionId));
        if (response.success && response.data) {
          setSessionTitle(response.data.chat_title || 'Chat');
        }
      } catch (err) {
        console.error('Error loading session:', err);
      }
    };
    loadSessionTitle();
  }, [sessionId]);

  // Handle scroll
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const div = e.currentTarget;
    const threshold = window.innerWidth <= 768
      ? CHAT_CONFIG.mobileAutoScrollThreshold
      : CHAT_CONFIG.autoScrollThreshold;
    const isAtBottom = div.scrollHeight - div.scrollTop <= div.clientHeight + threshold;

    if (!isAtBottom && !userScrolled) {
      setUserScrolled(true);
    } else if (isAtBottom && userScrolled) {
      setUserScrolled(false);
    }
  };

  // Handle send
  const handleSend = useCallback((files?: File[]) => {
    if (!inputValue.trim()) return;
    sendMessage(inputValue, files);
    setInputValue('');
  }, [inputValue, sendMessage]);

  // Global keydown handler
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') {
        return;
      }
      if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) {
        return;
      }
      const specialKeys = [
        'Escape', 'Enter', 'Tab', 'Backspace', 'Delete',
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Home', 'End', 'PageUp', 'PageDown',
        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
      ];
      if (specialKeys.includes(e.key)) {
        return;
      }
      if (textareaRef.current && e.key.length === 1) {
        e.preventDefault();
        textareaRef.current.focus();
        setInputValue((prev) => prev + e.key);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  if (error && messages.length === 0) {
    return (
      <div className="flex flex-col h-screen bg-[#242424]">
        <ChatHeader title={sessionTitle} />
        <div className="min-h-[calc(100vh-56px)] md:min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <p className="text-red-500 text-center">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row h-screen w-full relative overflow-hidden bg-[#242424]">
      {/* Main Chat Container */}
      <div className={`
        flex flex-col flex-1 min-w-0 w-full
        transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
        ${activeArtifact ? 'lg:mr-[40%] xl:mr-[40%]' : ''}
      `}>
        {/* Header */}
        <ChatHeader title={sessionTitle} />

        {/* Messages */}
        <div
          className="
            flex-1 overflow-y-auto overflow-x-hidden
            py-3 flex flex-col
            min-h-0
          "
          onScroll={handleScroll}
        >
          <div className="max-w-[800px] mx-auto w-full px-4 flex flex-col gap-1">
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                userName={userName}
                userInitial={userInitials}
                onRetry={retryMessage}
                onViewArtifact={setActiveArtifact}
                isLoading={isLoading}
                className="group"
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          disabled={isLoading || isInitializing}
          placeholder="Digite sua mensagem..."
        />
      </div>

      {/* Artifact Side Panel */}
      {(activeArtifact || isArtifactLoading) && (
        <ArtifactPanel
          artifact={activeArtifact}
          onClose={() => setActiveArtifact(null)}
          isLoading={isArtifactLoading && !activeArtifact}
          isUpdating={isUpdating}
        />
      )}

      {/* Session Loading Overlay */}
      {isInitializing && (
        <div className="absolute inset-0 bg-[#242424] flex items-center justify-center z-50">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-[#333333] border-t-[#F4CC04] rounded-full animate-spin" />
            <span className="text-[#666666] text-sm">Carregando conversa...</span>
          </div>
        </div>
      )}

      {/* Token Limit Modal */}
      {tokenLimitError && (
        <TokenLimitModal
          isOpen={!!tokenLimitError}
          onClose={closeTokenLimitModal}
          tokenLimitError={tokenLimitError}
        />
      )}
    </div>
  );
};
